import { ApiClient } from "@api/ApiClient";
import { Document, DocumentExtract } from "@components/documents/types";
import { reportError } from "@utils/observability/reportError";

export const listFilesApi = async (apiClient: ApiClient, sessionId: string): Promise<Document[]> => {
  const results = await apiClient.get(`/documents?sessionId=${sessionId}`);
  if (!results) {
    const error = new Error("No response from server");
    reportError("documents.list", error);
    throw error;
  }
  return results.json() as Promise<Document[]>;
};

interface UploadFileApiParams {
  apiClient: ApiClient;
  file: File;
  type: string;
  sessionId: string;
  workingDocumentId: string;
  partsList?: File | null;
}

export const uploadFileApi = async ({
  apiClient,
  file,
  type,
  sessionId,
  workingDocumentId,
  partsList = null,
}: UploadFileApiParams): Promise<Document> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("fileType", type);
  formData.append("sessionId", sessionId);
  formData.append("currentDocumentId", workingDocumentId);
  if (partsList) {
    formData.append("partsList", partsList);
  }
  const results = await apiClient.post("/document", formData);
  if (!results) {
    const error = new Error("No response from server");
    reportError("documents.upload", error);
    throw error;
  }
  return results.json() as Promise<Document>;
};

export const deleteFileApi = async (apiClient: ApiClient, sessionId: string, documentId: string): Promise<void> => {
  await apiClient.delete(
    `/document?sessionId=${encodeURIComponent(sessionId)}&documentId=${encodeURIComponent(documentId)}`
  );
};

interface EditFileApiParams {
  apiClient: ApiClient;
  sessionId: string;
  documentId: string;
  fileType: string;
  label: string;
  partsList?: string | null;
}

export const editFileApi = async ({
  apiClient,
  sessionId,
  documentId,
  fileType,
  label,
  partsList = null,
}: EditFileApiParams): Promise<DocumentExtract[]> => {
  const formData = new FormData();
  formData.append("sessionId", sessionId);
  formData.append("documentId", documentId);
  formData.append("label", label);
  formData.append("fileType", fileType);
  if (partsList) {
    formData.append("partsList", partsList);
  }
  const results = await apiClient.put("/document", formData);
  if (!results) {
    const error = new Error("No response from server");
    reportError("documents.editFile", error);
    throw error;
  }
  return results.json() as Promise<DocumentExtract[]>;
};

export const downloadFileApi = async (apiClient: ApiClient, sourcePath: string): Promise<string> => {
  const encodedPath = sourcePath.split("/").map(encodeURIComponent).join("/");
  const results = await apiClient.get(`/uspto_documents/${encodedPath}`);
  if (!results) {
    const error = new Error("No response from server");
    reportError("documents.download", error);
    throw error;
  }
  const body = await results.json();
  const url = body?.url;
  if (typeof url !== "string") {
    const error = new Error("Missing url in response");
    reportError("documents.download", error);
    throw error;
  }
  return url;
};
